import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Github, Linkedin, Mail, ArrowUp } from 'lucide-react';

const CYCLE_WORDS = ['SIGNAL.', 'SYSTEMS.', 'OUTCOMES.', 'DECISIONS.'];
const HOLD_MS = 2600;

// Flicker variants — restrained, not glitchy
const flickerEnter = {
  initial: { opacity: 0 },
  animate: {
    opacity: [0, 0.55, 0.9, 0.72, 1],
    transition: { duration: 0.42, times: [0, 0.12, 0.35, 0.62, 1], ease: 'linear' },
  },
};

const flickerExit = {
  exit: {
    opacity: [1, 0.25, 0.7, 0],
    transition: { duration: 0.28, times: [0, 0.2, 0.55, 1], ease: 'linear' },
  },
};

const Footer = () => {
  const [wordIdx, setWordIdx] = useState(0);
  const sigRef = useRef(null);
  const sigInView = useInView(sigRef, { once: true, margin: '-60px' });
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  useEffect(() => {
    const id = setInterval(() => setWordIdx(i => (i + 1) % CYCLE_WORDS.length), HOLD_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <footer
      className="relative border-t bg-bg-primary"
      style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      data-testid="footer"
    >
      {/* ── Cycling statement ───────────────────────────────── */}
      <div
        className="border-b"
        style={{ borderColor: 'rgba(255,255,255,0.05)' }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24 select-none">
          <p
            className="font-display font-medium leading-[0.88] tracking-[-0.025em] text-white mb-1"
            style={{ fontSize: 'clamp(50px, 9.5vw, 116px)' }}
          >
            FIND
          </p>

          {/* Cycling word — fixed height so layout never shifts */}
          <div
            className="relative overflow-hidden"
            style={{ height: 'clamp(44px, 8.5vw, 104px)' }}
          >
            <AnimatePresence mode="wait">
              <motion.p
                key={wordIdx}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={{ ...flickerEnter, ...flickerExit }}
                className="font-display font-medium leading-[0.88] tracking-[-0.025em] text-outline absolute top-0 left-0 whitespace-nowrap"
                style={{ fontSize: 'clamp(50px, 9.5vw, 116px)' }}
              >
                {CYCLE_WORDS[wordIdx]}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* ── Main footer row ──────────────────────────────────── */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-14">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          {/* Left */}
          <div>
            <h3 className="font-display text-xl font-medium mb-1.5 tracking-tight">
              Rayhan Patel
            </h3>
            <p className="text-text-secondary text-sm">
              AI Systems · Data Science · Analytics
            </p>
          </div>

          {/* Social links */}
          <div className="flex items-center gap-1 -mx-3">
            <motion.a
              href="https://github.com/rayhanpatel88"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-11 w-11 items-center justify-center text-text-secondary hover:text-white transition-colors duration-200"
              whileHover={{ scale: 1.1 }}
              aria-label="GitHub"
              data-testid="footer-github"
            >
              <Github className="w-[17px] h-[17px]" />
            </motion.a>
            <motion.a
              href="https://linkedin.com/in/rayhanpatel1"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-11 w-11 items-center justify-center text-text-secondary hover:text-white transition-colors duration-200"
              whileHover={{ scale: 1.1 }}
              aria-label="LinkedIn"
              data-testid="footer-linkedin"
            >
              <Linkedin className="w-[17px] h-[17px]" />
            </motion.a>
            <motion.a
              href="mailto:rayhanpatel88@gmail.com"
              className="flex h-11 w-11 items-center justify-center text-text-secondary hover:text-white transition-colors duration-200"
              whileHover={{ scale: 1.1 }}
              aria-label="Email"
              data-testid="footer-email"
            >
              <Mail className="w-[17px] h-[17px]" />
            </motion.a>
          </div>

          {/* Back to top */}
          <motion.button
            onClick={scrollToTop}
            className="flex items-center gap-2 py-3 text-text-secondary hover:text-white transition-colors duration-200 text-sm"
            whileHover={{ y: -2 }}
            data-testid="back-to-top-btn"
          >
            Back to top
            <ArrowUp className="w-4 h-4" />
          </motion.button>
        </div>

        {/* ── Bottom bar with signature ─────────────────────── */}
        <div
          className="mt-10 pt-8 border-t flex flex-col sm:flex-row justify-between items-end gap-6"
          style={{ borderColor: 'rgba(255,255,255,0.05)' }}
        >
          {/* Signature mark */}
          <motion.div
            ref={sigRef}
            initial={{ opacity: 0, x: -8 }}
            animate={sigInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <p
              className="leading-none mb-1.5"
              style={{
                fontFamily: "'Sacramento', cursive",
                fontSize: '2.1rem',
                color: 'rgba(255,255,255,0.28)',
                letterSpacing: '0.01em',
              }}
            >
              Rayhan Patel
            </p>
            <p className="text-text-secondary/45 text-[10px] tracking-[0.18em] uppercase">
              © {new Date().getFullYear()} · All rights reserved
            </p>
          </motion.div>

          <p className="text-text-secondary/60 text-xs tracking-wide">
            Leicester, UK · Open to Remote Worldwide
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
